import { useEffect, useState } from "react";
import { Download, X, Bell } from "lucide-react";
import { useAdminInstallPrompt } from "@/lib/adminPwa";

const DISMISS_KEY = "gc_admin_install_nudge_dismissed_until";
const DISMISS_DAYS = 7;
const MOBILE_BREAKPOINT_PX = 768;

/**
 * Thin top banner shown on mobile admin pages nudging the user to install
 * the dashboard as a PWA so they receive push notifications reliably.
 *
 * - Hides if already installed, if not on mobile, or if user dismissed
 *   within the last 7 days.
 * - On Android Chrome / Edge → fires `beforeinstallprompt`.
 * - On iOS Safari → opens an instructions modal.
 */
export default function AdminInstallNudge() {
  const { canInstall, installed, isIos, install } = useAdminInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (installed) {
      setVisible(false);
      return;
    }
    // Mobile-only — desktop already has the header "Install app" button.
    const isMobile = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT_PX}px)`,
    ).matches;
    if (!isMobile) {
      setVisible(false);
      return;
    }
    // Respect 7-day snooze
    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedUntil > Date.now()) {
      setVisible(false);
      return;
    }
    // Only show if we can actually install OR we can give iOS instructions
    setVisible(canInstall || isIos);
  }, [canInstall, installed, isIos]);

  const dismiss = () => {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setVisible(false);
  };

  const onInstall = async () => {
    if (isIos) {
      setShowIosHint(true);
    } else {
      const res = await install();
      if (res?.outcome === "accepted") {
        setVisible(false);
      }
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="admin-install-nudge"
        className="md:hidden bg-emerald-600 text-white px-4 py-2.5 flex items-center gap-3"
      >
        <div className="w-8 h-8 rounded-lg bg-white/15 inline-flex items-center justify-center flex-shrink-0">
          <Bell className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <div className="text-sm font-semibold">Install for push alerts</div>
          <div className="text-[11px] text-emerald-50/90 truncate">
            Never miss a customer chat — get instant notifications.
          </div>
        </div>
        <button
          type="button"
          onClick={onInstall}
          data-testid="admin-install-nudge-cta"
          className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-xs font-bold bg-white text-emerald-700 hover:bg-emerald-50 flex-shrink-0"
        >
          <Download className="w-3.5 h-3.5" />
          Install
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="admin-install-nudge-dismiss"
          className="h-8 w-8 -mr-1 rounded-lg hover:bg-white/10 inline-flex items-center justify-center flex-shrink-0"
          aria-label="Dismiss for 7 days"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {showIosHint && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setShowIosHint(false)}
          data-testid="admin-install-nudge-ios-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowIosHint(false)}
              className="absolute top-3 right-3 h-8 w-8 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="font-semibold text-slate-900 mb-3">Install on iPhone</div>
            <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside leading-relaxed">
              <li>Tap the <strong>Share</strong> button in Safari.</li>
              <li>Scroll down and tap <strong>“Add to Home Screen”</strong>.</li>
              <li>Tap <strong>Add</strong> in the top-right.</li>
            </ol>
            <p className="mt-4 text-xs text-slate-500">
              Push notifications &amp; offline access work like a native app once installed.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
